import {
  ArrowDownRight,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  Check,
  ChevronRight,
  CircleCheck,
  CircleDollarSign,
  CreditCard,
  Headset,
  RefreshCw,
  Menu,
  Printer,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Store,
  Truck,
  Users,
  Utensils,
  X,
  Zap,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef, useState } from "react";

import { Footer } from "@/components/landing/footer";
import { FaqList, LeadForm } from "@/components/landing/lead-form";
import { ScrollReveal } from "@/components/landing/scroll-reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePublicPlans } from "@/hooks/use-public-plans";
import { DEMO_WHATSAPP } from "@/lib/landing-config";
import { buildPlanFeatures, formatPlanPrice, getBillingSuffix, type PublicPlan } from "@/lib/plans";
import { cn } from "@/lib/utils";

const steps = [
  {
    index: "01",
    title: "Receba o pedido",
    text: "Pedidos online, balcão e salão chegam organizados para a equipe certa, sem depender de anotações soltas.",
    variant: "pos" as const,
    icon: ReceiptText,
  },
  {
    index: "02",
    title: "Atenda com ritmo",
    text: "O garçom lança pedidos pelo app e o balcão trabalha com uma visão clara do que precisa sair agora.",
    variant: "waiter" as const,
    icon: Users,
  },
  {
    index: "03",
    title: "Produza sem ruído",
    text: "Impressão automática, status visíveis e produtos organizados para reduzir conferências e retrabalho.",
    variant: "kitchen" as const,
    icon: Printer,
  },
  {
    index: "04",
    title: "Feche o dia sabendo",
    text: "Caixa, vendas e operação em uma visão simples para você decidir melhor no dia seguinte.",
    variant: "dashboard" as const,
    icon: BarChart3,
  },
];

const features = [
  {
    icon: Users,
    eyebrow: "01 / Salão",
    title: "Garçom com visão do salão",
    text: "Mesas, pedidos e atendimento presencial em um app direto ao ponto.",
    className: "md:col-span-7",
    variant: "waiter" as const,
  },
  {
    icon: CreditCard,
    eyebrow: "02 / Balcão",
    title: "PDV que acompanha o rush",
    text: "Venda mais rápido com uma operação de balcão organizada.",
    className: "md:col-span-5",
    variant: "pos" as const,
  },
  {
    icon: Truck,
    eyebrow: "03 / Entrega",
    title: "Delivery sem perder o controle",
    text: "Pedidos, entregadores e status da saída em um único fluxo.",
    className: "md:col-span-5",
    variant: "delivery" as const,
  },
  {
    icon: BarChart3,
    eyebrow: "04 / Gestão",
    title: "Dados que ajudam a decidir",
    text: "Acompanhe vendas, caixa, produtos e os gargalos que aparecem na operação.",
    className: "md:col-span-7",
    variant: "dashboard" as const,
  },
];

const appsSuite = [
  {
    icon: Users,
    eyebrow: "App Garçom",
    title: "Atendimento rápido no salão",
    text: "O garçom lança pedidos, acompanha mesas e agiliza o atendimento sem sair do salão.",
    bullets: [
      "Pedido direto na mesa",
      "Status da cozinha em tempo real",
      "Fechamento de comanda simplificado",
    ],
    screenshot: {
      src: "/landing/apps/app-garcom.png",
      alt: "Tela do app Garçom com a lista de mesas do salão e o status de cada uma: ocupada, livre ou inativa",
    },
  },
  {
    icon: Truck,
    eyebrow: "App Motoboy",
    title: "Entrega sob controle, do pedido à porta",
    text: "O entregador recebe a corrida, o endereço e atualiza o status sem precisar ligar para a loja.",
    bullets: [
      "Endereço e rota na tela",
      "Atualização do status da entrega",
      "Histórico de corridas do dia",
    ],
    screenshot: {
      src: "/landing/apps/app-motoboy.png",
      alt: "Tela do app Motoboy com o endereço de entrega, itens do pedido e botões para abrir no Waze ou Maps",
    },
  },
  {
    icon: BarChart3,
    eyebrow: "App Gestão da Loja",
    title: "Toda a operação na palma da mão",
    text: "O gestor acompanha vendas, caixa e equipe de qualquer lugar, sem depender do computador.",
    bullets: [
      "Vendas e caixa em tempo real",
      "Controle de caixa diário e mensal",
      "Relatórios de desempenho",
    ],
    screenshot: {
      src: "/landing/apps/app-gestao.png",
      alt: "Tela do app Gestão da Loja com pedidos novos, em andamento, finalizados e receita do dia",
    },
  },
] as const;

const operationVisuals = {
  waiter: {
    src: "/landing/operation/salon.webp",
    alt: "Profissional do salão usando um tablet enquanto acompanha mesas em um restaurante movimentado",
  },
  pos: {
    src: "/landing/operation/counter.webp",
    alt: "Profissional usando um ponto de venda em um balcão de restaurante durante o movimento",
  },
  delivery: {
    src: "/landing/operation/delivery.webp",
    alt: "Profissionais realizando a entrega de uma sacola na porta do restaurante",
  },
  kitchen: {
    src: "/landing/operation/kitchen.webp",
    alt: "Profissional conferindo um pedido impresso enquanto a equipe prepara pratos em uma cozinha",
  },
  dashboard: {
    src: "/landing/operation/management.webp",
    alt: "Pessoa responsável pela operação analisando o restaurante em um laptop sobre a mesa",
  },
} as const;

const valueProps = [
  {
    icon: CircleDollarSign,
    title: "100% da sua venda",
    text: "No seu canal próprio, o valor da venda fica no seu caixa — sem comissão da plataforma.",
  },
  {
    icon: Headset,
    title: "Suporte humano",
    text: "Fale com uma pessoa de verdade quando precisar organizar a operação.",
  },
  {
    icon: BadgeCheck,
    title: "Sem fidelidade",
    text: "Você fica porque a plataforma funciona para o seu negócio, não por contrato longo.",
  },
  {
    icon: RefreshCw,
    title: "Atualizações constantes",
    text: "A plataforma evolui com melhorias contínuas para acompanhar seu dia a dia.",
  },
  {
    icon: ShieldCheck,
    title: "Plataforma segura",
    text: "Seus pedidos e dados ficam protegidos em uma base feita para operar com confiança.",
  },
] as const;

const desktopScreens = [
  {
    src: "/landing/dashboard.webp",
    label: "Visão geral",
    text: "Vendas, pedidos recentes e produtos mais vendidos em uma única leitura.",
    alt: "Dashboard Innovally com gráfico de vendas da semana e pedidos recentes",
  },
  {
    src: "/landing/orders.webp",
    label: "Pedidos em tempo real",
    text: "A equipe recebe, aceita e acompanha cada pedido sem perder o ritmo.",
    alt: "Tela Innovally de gerenciamento de pedidos com pedido novo e ação para aceitar",
  },
] as const;

const pdvScreens = [
  {
    src: "/landing/pdv-home.webp",
    label: "Cardápio",
    text: "Uma vitrine própria para o cliente encontrar e escolher mais rápido.",
    alt: "Tela mobile do cardápio digital Açaí do Motoca com busca, categorias e produtos",
  },
  {
    src: "/landing/pdv-product.webp",
    label: "Produto",
    text: "Tamanhos, sabores e adicionais organizados no mesmo fluxo.",
    alt: "Tela mobile de detalhes do produto Açaí Trufado com seleção de tamanho e sabor",
  },
  {
    src: "/landing/pdv-addons.webp",
    label: "Personalização",
    text: "O cliente monta o pedido do jeito que quer, sem troca de mensagens.",
    alt: "Tela mobile de personalização de produto com complementos",
  },
  {
    src: "/landing/pdv-cart.webp",
    label: "Carrinho",
    text: "Itens, entrega e total reunidos antes de confirmar o pedido.",
    alt: "Tela mobile do carrinho com escolha de entrega e resumo do pedido",
  },
  {
    src: "/landing/pdv-review.webp",
    label: "Revisão",
    text: "Pedido, entrega e pagamento reunidos em uma revisão clara.",
    alt: "Tela mobile de revisão do pedido com itens, entrega e pagamento PIX",
  },
] as const;

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <main className="landing-page scroll-pb-[calc(6rem+env(safe-area-inset-bottom))] overflow-hidden bg-background pb-[calc(5.75rem+env(safe-area-inset-bottom))] text-foreground selection:bg-primary/30 lg:pb-0">
      <LandingHeader mobileMenuOpen={mobileMenuOpen} onMobileMenuChange={setMobileMenuOpen} />
      <HeroSection onCloseMenu={() => setMobileMenuOpen(false)} />
      <AudienceStrip />
      <MarginSection />
      <FeaturesSection />
      <AppsSuiteSection />
      <OperationStory />
      <ProductShowcase />
      <OfferSection />
      <FaqSection />
      <FinalCta />
      <Footer />
      <MobileStickyCta />
    </main>
  );
}

function LandingHeader({
  mobileMenuOpen,
  onMobileMenuChange,
}: {
  mobileMenuOpen: boolean;
  onMobileMenuChange: (open: boolean) => void;
}) {
  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-5">
      <div className="mx-auto flex max-w-7xl items-center justify-between rounded-2xl border border-white/10 bg-[#171717]/80 px-4 py-3 shadow-2xl shadow-black/10 backdrop-blur-xl sm:px-5">
        <a href="#top" className="flex items-center gap-2.5" aria-label="Innovally, início">
          <img
            src="/logo-icon.png"
            alt=""
            className="h-8 w-8 object-contain"
            width={220}
            height={220}
            loading="eager"
          />
          <span className="font-display text-sm font-bold tracking-[-0.03em] text-white sm:text-base">
            Innovally
          </span>
        </a>

        <nav
          className="hidden items-center gap-7 text-xs font-medium text-white/55 lg:flex"
          aria-label="Navegação principal"
        >
          <a className="transition-colors hover:text-white" href="#vantagens">
            Vantagens
          </a>
          <a className="transition-colors hover:text-white" href="#solucoes">
            Soluções
          </a>
          <a className="transition-colors hover:text-white" href="#apps">
            Apps
          </a>
          <a className="transition-colors hover:text-white" href="#pdv">
            PDV
          </a>
          <a className="transition-colors hover:text-white" href="#como-funciona">
            Como funciona
          </a>
          <a className="transition-colors hover:text-white" href="#oferta">
            Oferta
          </a>
          <a className="transition-colors hover:text-white" href="#faq">
            FAQ
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild size="sm" className="hidden rounded-lg px-4 text-xs sm:inline-flex">
            <a
              href={
                DEMO_WHATSAPP
                  ? `https://wa.me/${DEMO_WHATSAPP}?text=${encodeURIComponent("Olá, Innovally! Quero conhecer o plano de R$ 79,90.")}`
                  : "#contato"
              }
              target={DEMO_WHATSAPP ? "_blank" : undefined}
              rel={DEMO_WHATSAPP ? "noreferrer" : undefined}
            >
              Conhecer o plano <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => onMobileMenuChange(!mobileMenuOpen)}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white/70 transition-colors hover:bg-white/10 lg:hidden"
          >
            {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mx-auto mt-2 max-w-7xl rounded-2xl border border-white/10 bg-[#171717]/95 p-3 shadow-2xl backdrop-blur-xl lg:hidden"
            aria-label="Navegação móvel"
          >
            {[
              ["#vantagens", "Vantagens"],
              ["#solucoes", "Soluções"],
              ["#apps", "Apps"],
              ["#pdv", "PDV"],
              ["#como-funciona", "Como funciona"],
              ["#oferta", "Oferta"],
              ["#faq", "FAQ"],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => onMobileMenuChange(false)}
                className="flex items-center justify-between rounded-xl px-3 py-3 text-sm text-white/70 hover:bg-white/10 hover:text-white"
              >
                {label}
                <ChevronRight className="h-4 w-4" />
              </a>
            ))}
            <a
              href="#contato"
              onClick={() => onMobileMenuChange(false)}
              className="mt-1 flex items-center justify-center rounded-xl bg-primary px-3 py-3 text-sm font-semibold text-primary-foreground"
            >
              Conhecer o plano
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function HeroSection({ onCloseMenu }: { onCloseMenu: () => void }) {
  const heroRef = useRef<HTMLElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, shouldReduceMotion ? 0 : -110]);
  const visualRotate = useTransform(scrollYProgress, [0, 1], [0, shouldReduceMotion ? 0 : -2]);

  return (
    <section
      ref={heroRef}
      id="top"
      className="relative isolate min-h-[760px] overflow-hidden bg-[#171717] pt-32 text-white sm:min-h-[860px] sm:pt-40"
    >
      <div className="landing-grid pointer-events-none absolute inset-0 opacity-35" />
      <div className="pointer-events-none absolute -right-40 top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 left-1/4 h-96 w-96 rounded-full bg-white/[0.035] blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-28 sm:px-8 lg:grid-cols-[0.88fr_1.12fr] lg:gap-10 lg:px-10 lg:pb-36">
        <div className="relative z-10 max-w-2xl">
          <div className="landing-hero-item landing-hero-item-1">
            <Badge
              variant="outline"
              className="mb-7 max-w-full whitespace-normal rounded-full border-primary/35 bg-primary/10 px-3 py-1.5 text-center text-[10px] uppercase leading-4 tracking-[0.16em] text-primary sm:tracking-[0.2em]"
            >
              Canal próprio + plano completo por R$ 79,90
            </Badge>
          </div>
          <h1 className="landing-hero-item landing-hero-item-2 max-w-[12ch] font-display text-[clamp(3.6rem,8vw,7.2rem)] font-semibold leading-[0.91] tracking-[-0.075em]">
            <span className="block">Venda direto.</span>
            <span className="block text-primary">Proteja sua margem.</span>
          </h1>
          <p className="landing-hero-item landing-hero-item-3 mt-7 max-w-lg text-base leading-7 text-white/70 sm:text-lg">
            Seu cardápio online, PDV e gestão em um só lugar — sem comissão nos pedidos do seu canal
            próprio.
          </p>
          <div className="landing-hero-item landing-hero-item-4 mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl px-5 text-sm shadow-xl shadow-primary/15"
            >
              <a href="#contato" onClick={onCloseMenu}>
                Quero meu canal próprio <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="lg"
              className="h-12 justify-start rounded-xl px-4 text-sm text-white/65 hover:bg-white/10 hover:text-white sm:justify-center"
            >
              <a href="#pdv">
                Ver o produto em ação <ArrowDownRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
          <div className="landing-hero-item landing-hero-item-5 mt-8 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-white/55">
            <span className="flex items-center gap-1.5">
              <CircleCheck className="h-3.5 w-3.5 text-primary" /> 100% da venda no seu canal
            </span>
            <span className="flex items-center gap-1.5">
              <CircleCheck className="h-3.5 w-3.5 text-primary" /> Sem fidelidade
            </span>
          </div>
        </div>

        <motion.div
          style={{ y: visualY, rotate: visualRotate }}
          className="relative mx-auto w-full max-w-2xl pb-10 lg:ml-auto"
        >
          <div className="relative min-h-[430px] sm:min-h-[520px]">
            <div
              className="absolute -inset-8 rounded-[3rem] bg-primary/10 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative z-10 ml-auto w-[min(100%,640px)] rotate-[1.2deg] rounded-[2rem] border border-white/15 bg-[#0b0d12]/95 p-2 shadow-[0_36px_100px_rgba(0,0,0,0.44)] backdrop-blur-sm sm:rounded-[2.35rem] sm:p-3">
              <div className="overflow-hidden rounded-[1.4rem] bg-[#e6e8eb] sm:rounded-[1.7rem]">
                <img
                  src="/landing/dashboard.webp"
                  alt="Dashboard Innovally com vendas da semana, pedidos recentes e produtos mais vendidos"
                  className="block h-auto w-full"
                  width={3008}
                  height={2312}
                  loading="eager"
                />
              </div>
            </div>
            <div className="absolute -bottom-1 left-0 z-20 w-[clamp(210px,34vw,300px)] -rotate-[3deg] rounded-[1.5rem] border border-white/15 bg-[#0b0d12]/95 p-2 shadow-[0_28px_70px_rgba(0,0,0,0.52)] sm:-bottom-4 sm:left-5 sm:rounded-[1.7rem]">
              <div className="overflow-hidden rounded-[1.1rem] bg-white">
                <img
                  src="/landing/pdv-product.webp"
                  alt="Tela mobile do canal próprio com produto, preço e opções de personalização"
                  className="block aspect-[16/10] h-auto w-full object-cover object-top"
                  width={1320}
                  height={2868}
                  loading="lazy"
                />
              </div>
            </div>
            <div className="relative mt-4 w-full rounded-[1.35rem] border border-white/15 bg-[#0b0d12]/95 p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.38)] sm:absolute sm:bottom-0 sm:right-0 sm:mt-0 sm:w-[min(58%,350px)] sm:rotate-[-1.5deg] sm:p-2">
              <div className="flex items-center justify-between px-2 pb-2 pt-1 text-[9px] uppercase tracking-[0.16em] text-white/55">
                <span>Pedidos em tempo real</span>
                <span className="flex items-center gap-1.5 text-[#54dca0]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#54dca0]" /> online
                </span>
              </div>
              <div className="overflow-hidden rounded-[1rem] bg-[#e6e8eb]">
                <img
                  src="/landing/orders.webp"
                  alt="Tela de pedidos Innovally com pedido novo e botão para aceitar"
                  className="block h-auto w-full"
                  width={3024}
                  height={1532}
                  loading="eager"
                />
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[2.5rem] border border-primary/20 opacity-60" />
        </motion.div>
      </div>

      <div className="relative mx-auto flex max-w-7xl items-center gap-4 px-5 pb-7 text-[10px] uppercase tracking-[0.2em] text-white/45 sm:px-8 lg:px-10">
        <span className="h-px w-10 bg-primary" />
        <span>Gestão feita para o movimento real</span>
      </div>
    </section>
  );
}

function AudienceStrip() {
  return (
    <section className="border-y border-white/10 bg-[#171717] px-5 py-6 text-white/45 sm:px-8 lg:px-10">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-8 gap-y-4 text-xs font-medium">
        <span className="text-white/45">Para quem vive o rush</span>
        {[
          [Utensils, "Restaurantes"],
          [Store, "Lanchonetes"],
          [Sparkles, "Açaiterias"],
          [Truck, "Delivery"],
        ].map(([Icon, label]) => (
          <span key={label as string} className="flex items-center gap-2">
            <Icon className="h-3.5 w-3.5 text-primary/80" />
            {label as string}
          </span>
        ))}
      </div>
    </section>
  );
}

function MarginSection() {
  return (
    <section
      id="vantagens"
      aria-labelledby="margin-title"
      className="relative scroll-mt-28 overflow-hidden bg-[#171717] px-5 py-24 text-white sm:px-8 sm:py-32 lg:px-10"
    >
      <div className="pointer-events-none absolute -right-40 top-0 h-[34rem] w-[34rem] rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-48 w-48 rounded-full bg-white/[0.04] blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="grid items-end gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <ScrollReveal>
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
              O que muda no seu caixa
            </p>
            <h2
              id="margin-title"
              className="max-w-xl font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl"
            >
              Menos taxa. <span className="text-primary">Mais margem.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-7 text-white/50">
              Em vez de entregar parte de cada pedido para um marketplace, transforme seu canal
              próprio em uma frente de venda que trabalha para o seu negócio.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm font-medium text-white/75">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary">
                <CircleDollarSign className="h-4 w-4" />
              </span>
              O relacionamento com o cliente continua sendo seu.
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Card className="relative overflow-hidden rounded-[2rem] border-white/10 bg-white/[0.06] p-5 text-white shadow-2xl shadow-black/20 sm:p-7">
              <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
              <div className="relative flex flex-wrap items-start justify-between gap-4">
                <div>
                  <Badge className="rounded-full bg-primary px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-primary-foreground">
                    Comparativo de modelo
                  </Badge>
                  <p className="mt-5 text-sm text-white/45">Em um pedido de R$ 100,00</p>
                </div>
                <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-white/45">
                  Exemplo ilustrativo
                </span>
              </div>

              <div className="relative mt-7 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-primary/35 bg-primary/10 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-white/85">Canal próprio Innovally</p>
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                  <p className="mt-8 font-display text-4xl font-semibold tracking-[-0.06em] text-white">
                    R$ 100<span className="text-xl text-primary">,00</span>
                  </p>
                  <p className="mt-2 text-xs leading-5 text-primary/90">
                    100% da venda no seu caixa*
                  </p>
                  <div className="mt-6 h-2 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full w-full rounded-full bg-primary" />
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-white/70">
                      iFood e outras plataformas
                    </p>
                    <span className="text-[10px] uppercase tracking-[0.12em] text-white/55">
                      Bruto
                    </span>
                  </div>
                  <p className="mt-8 font-display text-4xl font-semibold tracking-[-0.06em] text-white/60">
                    R$ 100<span className="text-xl text-white/55">,00</span>
                  </p>
                  <p className="mt-2 text-xs leading-5 text-white/55">
                    Repasse varia após comissão e taxas
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/45">
                    <span className="h-2 w-2 rounded-full bg-white/30" />
                    Comissão e taxas variam conforme o canal
                  </div>
                </div>
              </div>

              <p className="relative mt-5 text-[11px] leading-5 text-white/55">
                * Considerando o canal próprio e sem comissão da plataforma. O valor líquido final
                ainda pode variar conforme pagamento, entrega e condições do seu negócio.
              </p>
            </Card>
          </ScrollReveal>
        </div>

        <div className="relative mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {valueProps.map((item, index) => {
            const Icon = item.icon;
            return (
              <ScrollReveal key={item.title} delay={index * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="h-full rounded-2xl border border-white/10 bg-white/[0.035] p-5 transition-colors hover:border-primary/35 hover:bg-white/[0.06]"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <h3 className="mt-5 text-sm font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-xs leading-5 text-white/55">{item.text}</p>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section
      id="solucoes"
      className="landing-light relative scroll-mt-28 bg-background px-5 py-28 text-foreground sm:px-8 sm:py-36 lg:px-10"
    >
      <div className="mx-auto max-w-7xl">
        <ScrollReveal className="max-w-2xl">
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
            Um sistema. Cada frente da operação.
          </p>
          <h2 className="font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
            O seu restaurante em uma visão mais simples.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground">
            Quando cada parte conversa com a outra, o atendimento ganha ritmo, a equipe ganha
            clareza e você volta a ter tempo para cuidar do crescimento.
          </p>
        </ScrollReveal>

        <div className="mt-16 grid gap-4 md:grid-cols-12">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }: { feature: (typeof features)[number]; index: number }) {
  const Icon = feature.icon;
  const visual = operationVisuals[feature.variant];

  return (
    <ScrollReveal delay={index * 0.06} className={feature.className}>
      <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.25 }} className="group h-full">
        <Card className="relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-[1.6rem] border-border/70 bg-card p-6 shadow-none transition-colors duration-300 group-hover:border-primary/35 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
              {feature.eyebrow}
            </span>
          </div>

          <div className="relative z-10 mt-8 max-w-md">
            <h3 className="font-display text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">
              {feature.title}
            </h3>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{feature.text}</p>
          </div>

          <div className="relative mt-auto overflow-hidden rounded-[1.2rem] border border-border/70 bg-muted shadow-[0_18px_50px_rgba(0,0,0,0.12)]">
            <img
              src={visual.src}
              alt={visual.alt}
              className="block aspect-[3/2] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
              width={1536}
              height={1024}
              loading="lazy"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"
              aria-hidden="true"
            />
          </div>
        </Card>
      </motion.div>
    </ScrollReveal>
  );
}

function AppsSuiteSection() {
  return (
    <section
      id="apps"
      aria-labelledby="apps-title"
      className="relative scroll-mt-28 overflow-hidden bg-[#171717] px-5 py-24 text-white sm:px-8 sm:py-32 lg:px-10"
    >
      <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-primary/12 blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <ScrollReveal className="max-w-2xl">
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
            Um app para cada função
          </p>
          <h2
            id="apps-title"
            className="font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl"
          >
            Os apps que colocam sua equipe no mesmo ritmo.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-7 text-white/50">
            Garçom, motoboy e gestor: cada um com o app certo para o seu momento da operação, tudo
            conectado em uma única plataforma.
          </p>
        </ScrollReveal>

        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {appsSuite.map((app, index) => {
            const Icon = app.icon;
            return (
              <ScrollReveal key={app.eyebrow} delay={index * 0.06}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.25 }}
                  className="flex h-full flex-col rounded-[1.6rem] border border-white/10 bg-white/[0.045] p-6 transition-colors hover:border-primary/35"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="mt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                    {app.eyebrow}
                  </span>
                  <h3 className="mt-2 font-display text-xl font-semibold tracking-[-0.03em] text-white">
                    {app.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-white/55">{app.text}</p>
                  <ul className="mt-5 space-y-2 border-t border-white/10 pt-5">
                    {app.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-2 text-xs leading-5 text-white/60"
                      >
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex justify-center">
                    <div className="w-full max-w-[190px] overflow-hidden rounded-[1.1rem] border border-white/10 bg-white/[0.04] p-1.5 shadow-[0_18px_45px_rgba(0,0,0,0.35)]">
                      <div className="overflow-hidden rounded-[0.85rem] bg-white/95">
                        <img
                          src={app.screenshot.src}
                          alt={app.screenshot.alt}
                          className="block h-auto w-full"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function OperationStory() {
  const storyRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: storyRef, offset: ["start start", "end end"] });
  const progress = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section
      ref={storyRef}
      id="como-funciona"
      className="relative scroll-mt-28 bg-[#171717] px-5 py-28 text-white sm:px-8 sm:py-36 lg:px-10"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-16 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24">
          <div className="lg:sticky lg:top-32 lg:h-fit">
            <ScrollReveal>
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
                Como funciona
              </p>
              <h2 className="max-w-lg font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
                Do pedido ao caixa, sem perder o fio.
              </h2>
              <p className="mt-6 max-w-md text-base leading-7 text-white/50">
                Uma operação mais previsível começa quando toda a equipe enxerga o mesmo momento — e
                sabe qual é o próximo passo.
              </p>
            </ScrollReveal>

            <div className="mt-10 hidden items-center gap-4 lg:flex">
              <div className="relative h-1 w-32 overflow-hidden rounded-full bg-white/10">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full bg-primary"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-white/55">
                SCROLL / OPERAÇÃO
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="relative space-y-8 lg:space-y-14">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.article
                    key={step.index}
                    onViewportEnter={() => setActiveStep(index)}
                    viewport={{ amount: 0.55, once: false }}
                    className={cn(
                      "group relative overflow-hidden rounded-[1.8rem] border border-white/10 bg-white/[0.045] p-2 transition-colors duration-500 sm:p-3",
                      activeStep === index && "border-primary/45",
                    )}
                  >
                    <div className="grid gap-5 p-3 sm:p-4 md:grid-cols-[0.78fr_1.22fr] md:items-center md:gap-7">
                      <div className="px-1 py-2 sm:px-2">
                        <div className="mb-5 flex items-center gap-3">
                          <span
                            className={cn(
                              "flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/45",
                              activeStep === index &&
                                "border-primary/30 bg-primary/10 text-primary",
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.18em] text-white/55">
                            {step.index}
                          </span>
                        </div>
                        <h3 className="font-display text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">
                          {step.title}
                        </h3>
                        <p className="mt-3 text-sm leading-6 text-white/50">{step.text}</p>
                      </div>
                      <div className="relative overflow-hidden rounded-[1.35rem] bg-[#0d0e10]">
                        <img
                          src={operationVisuals[step.variant].src}
                          alt={operationVisuals[step.variant].alt}
                          className="block aspect-[16/10] h-auto w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                          width={1536}
                          height={1024}
                          loading="lazy"
                        />
                        <div
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent"
                          aria-hidden="true"
                        />
                        <span className="absolute bottom-3 left-3 rounded-full border border-white/15 bg-[#171717]/75 px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] text-white/80 backdrop-blur-md">
                          {step.index} / operação
                        </span>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductShowcase() {
  return (
    <section
      id="pdv"
      className="relative scroll-mt-28 overflow-hidden bg-[#111216] px-5 py-28 text-white sm:px-8 sm:py-36 lg:px-10"
    >
      <img
        src="/landing/showcase-backdrop-v1.webp"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-70"
        loading="lazy"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,transparent_0%,rgba(17,18,22,0.16)_45%,#111216_92%)]" />
      <div className="relative mx-auto max-w-7xl">
        <div className="grid items-end gap-8 lg:grid-cols-[1fr_0.75fr]">
          <ScrollReveal>
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
              Produto que dá vontade de usar
            </p>
            <h2 className="max-w-xl font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
              Sua operação, vista com clareza.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="lg:justify-self-end">
            <p className="max-w-md text-base leading-7 text-white/50">
              Da gestão dos pedidos ao último toque no celular, o Innovally conecta a equipe e o
              cliente em uma experiência só.
            </p>
          </ScrollReveal>
        </div>

        <div className="mt-14 grid items-start gap-8 lg:grid-cols-[1.12fr_0.88fr]">
          <ScrollReveal className="relative min-w-0">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Gestão da operação
            </p>
            <div
              className="absolute -inset-8 rounded-[3rem] bg-primary/10 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative rounded-[2rem] border border-white/15 bg-[#0b0d12]/90 p-2 shadow-[0_36px_100px_rgba(0,0,0,0.42)] backdrop-blur-sm sm:rounded-[2.35rem] sm:p-3">
              <div className="overflow-hidden rounded-[1.4rem] bg-[#e6e8eb] sm:rounded-[1.7rem]">
                <img
                  src={desktopScreens[0].src}
                  alt={desktopScreens[0].alt}
                  className="block h-auto w-full"
                  width={3008}
                  height={2312}
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-between gap-4 px-3 pb-1 pt-4 sm:px-4">
                <span className="text-sm font-semibold text-white/85">Visão geral</span>
                <span className="text-[10px] uppercase tracking-[0.16em] text-white/45">
                  gestão da operação
                </span>
              </div>
            </div>
            <div className="absolute -bottom-5 left-5 hidden items-center gap-3 rounded-2xl border border-white/15 bg-[#181a20]/90 px-4 py-3 shadow-xl backdrop-blur-md sm:flex">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#26c281]/15 text-[#54dca0]">
                <CircleCheck size={18} />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.16em] text-white/55">
                  Visão em tempo real
                </span>
                <span className="block text-sm font-semibold text-white/85">Tudo sob controle</span>
              </span>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} className="relative min-w-0 lg:pt-10">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Canal do cliente
            </p>
            <div
              className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {[pdvScreens[1], pdvScreens[4]].map((screen) => (
                <div
                  key={screen.label}
                  className="group overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.05] p-2 transition-colors hover:border-primary/35"
                >
                  <div className="overflow-hidden rounded-[1.2rem] bg-white/95">
                    <img
                      src={screen.src}
                      alt={screen.alt}
                      className="block aspect-[0.82] w-full object-cover object-top transition duration-700 group-hover:scale-[1.03]"
                      width={1320}
                      height={2868}
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 px-2 pb-2 pt-3">
                    <span className="text-xs font-semibold text-white/85">{screen.label}</span>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-primary/80">
                      canal próprio
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>

        <div className="mt-20 grid gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
          <ScrollReveal className="min-w-0">
            <div className="rounded-[1.8rem] border border-white/10 bg-white/[0.045] p-2 sm:p-3">
              <div className="flex items-center justify-between px-3 pb-3 pt-1 text-[10px] uppercase tracking-[0.16em] text-white/55 sm:px-4">
                <span>Operação</span>
                <span className="flex items-center gap-2 text-[#54dca0]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#54dca0]" /> online
                </span>
              </div>
              <div className="overflow-hidden rounded-[1.35rem] bg-[#e6e8eb]">
                <img
                  src={desktopScreens[1].src}
                  alt={desktopScreens[1].alt}
                  className="block h-auto w-full"
                  width={3024}
                  height={1532}
                  loading="lazy"
                />
              </div>
            </div>
            <p className="mt-4 text-xs leading-5 text-white/55">
              Pedidos novos, em andamento e finalizados em uma visão que a equipe entende de
              primeira.
            </p>
          </ScrollReveal>

          <div className="min-w-0">
            <ScrollReveal>
              <div className="max-w-lg border-l border-primary/45 pl-5 sm:pl-7">
                <p className="text-2xl font-medium leading-tight tracking-[-0.04em] text-white sm:text-3xl">
                  Do cardápio ao pedido confirmado, cada etapa conversa com a próxima.
                </p>
                <p className="mt-5 max-w-md text-sm leading-6 text-white/45">
                  São interfaces reais do produto, pensadas para reduzir dúvida, acelerar a operação
                  e deixar a marca presente em todos os pontos de contato.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1} className="mt-10">
              <div className="grid gap-3 sm:grid-cols-2">
                {pdvScreens.slice(2, 4).map((screen, index) => (
                  <div
                    key={screen.label}
                    className="group rounded-[1.35rem] border border-white/10 bg-white/[0.045] p-2 transition-colors hover:border-primary/35"
                  >
                    <div className="overflow-hidden rounded-[1rem] bg-white/95">
                      <img
                        src={screen.src}
                        alt={screen.alt}
                        className="block aspect-[0.4614] w-full object-cover object-top transition duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                        width={1320}
                        height={2868}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3 px-2 pb-2 pt-3">
                      <span className="text-xs font-semibold text-white/85">{screen.label}</span>
                      <span className="font-mono text-[10px] text-primary/80">0{index + 2}</span>
                    </div>
                    <p className="px-2 pb-2 text-[11px] leading-4 text-white/55">{screen.text}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>

        <ScrollReveal delay={0.16} className="mt-10">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-5 text-xs text-white/55">
            <span className="inline-flex items-center gap-2">
              <BadgeCheck size={15} className="text-primary" /> interfaces reais do Innovally
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-white/20 sm:block" />
            <span>personalizável para a operação de cada negócio</span>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function OfferSection() {
  const { data: plans = [], isLoading, isError } = usePublicPlans();

  return (
    <section
      id="oferta"
      className="relative scroll-mt-28 overflow-hidden bg-[#171717] px-5 py-28 text-white sm:px-8 sm:py-36 lg:px-10"
    >
      <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-primary/12 blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
              Planos
            </p>
            <h2 className="font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
              Escolha o plano ideal para a sua operação.
            </h2>
            <p className="mx-auto mt-6 max-w-md text-base leading-7 text-white/50">
              Canal próprio, pedidos, PDV e gestão em um só lugar, sem comissão da plataforma e sem
              fidelidade.
            </p>
          </div>
        </ScrollReveal>

        <div className="mt-14">
          {isLoading ? (
            <PlansSkeleton />
          ) : isError || plans.length === 0 ? (
            <PlansUnavailable />
          ) : (
            <div
              className={cn(
                "mx-auto grid gap-6",
                plans.length === 1 && "max-w-md",
                plans.length === 2 && "max-w-4xl sm:grid-cols-2",
                plans.length >= 3 && "sm:grid-cols-2 lg:grid-cols-3",
              )}
            >
              {plans.map((plan, index) => (
                <ScrollReveal key={plan.id} delay={0.05 * index}>
                  <PlanCard plan={plan} />
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>

        <div className="mx-auto mt-10 flex max-w-xl items-start justify-center gap-2 text-center text-xs leading-5 text-white/55">
          <Headset className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>Sem fidelidade e com suporte humano para você começar com segurança.</span>
        </div>
      </div>
    </section>
  );
}

function buildPlanContactUrl(plan: PublicPlan) {
  const message = `Olá, Innovally! Quero conhecer o plano ${plan.name} de ${formatPlanPrice(plan.price)}.`;
  return DEMO_WHATSAPP
    ? `https://wa.me/${DEMO_WHATSAPP}?text=${encodeURIComponent(message)}`
    : "#contato";
}

function PlanCard({ plan }: { plan: PublicPlan }) {
  const features = buildPlanFeatures(plan);
  const contactUrl = buildPlanContactUrl(plan);

  return (
    <Card className="relative flex h-full flex-col overflow-hidden rounded-[2rem] border-white/10 bg-white/[0.06] p-6 text-white shadow-2xl shadow-black/20 sm:p-8">
      <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-primary/15 blur-3xl" />

      <div className="relative flex items-start justify-between gap-3">
        <h3 className="font-display text-2xl font-semibold tracking-tight">{plan.name}</h3>
        {plan.trial_days > 0 ? (
          <Badge className="shrink-0 rounded-full bg-primary px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-primary-foreground">
            {plan.trial_days} {plan.trial_days === 1 ? "dia grátis" : "dias grátis"}
          </Badge>
        ) : null}
      </div>

      <p className="relative mt-5 font-display text-5xl font-semibold tracking-[-0.06em]">
        {formatPlanPrice(plan.price)}
        <span className="ml-2 text-lg font-normal tracking-normal text-white/45">
          {getBillingSuffix(plan.billing_cycle)}
        </span>
      </p>

      {plan.description ? (
        <p className="relative mt-4 text-sm leading-6 text-white/55">{plan.description}</p>
      ) : null}

      <div className="relative my-6 h-px bg-white/10" />

      <ul className="relative flex-1 space-y-2.5">
        {features.map((feature) => (
          <li
            key={feature.label}
            className={cn(
              "flex items-start gap-2 text-sm",
              feature.included ? "text-white/75" : "text-white/30",
            )}
          >
            {feature.included ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            ) : (
              <X className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span className="flex-1">{feature.label}</span>
            {feature.comingSoon && feature.included ? (
              <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/60">
                Em breve
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      <Button asChild size="lg" className="relative mt-8 h-12 w-full rounded-xl">
        <a
          href={contactUrl}
          target={DEMO_WHATSAPP ? "_blank" : undefined}
          rel={DEMO_WHATSAPP ? "noreferrer" : undefined}
        >
          Quero este plano <ArrowUpRight className="h-4 w-4" />
        </a>
      </Button>
    </Card>
  );
}

function PlansSkeleton() {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="h-[34rem] animate-pulse rounded-[2rem] border border-white/10 bg-white/[0.04]"
        />
      ))}
    </div>
  );
}

/** API fora do ar ou sem planos ativos: a seção continua útil com o contato. */
function PlansUnavailable() {
  return (
    <Card className="mx-auto max-w-lg rounded-[2rem] border-white/10 bg-white/[0.06] p-8 text-center text-white">
      <p className="font-display text-2xl font-semibold tracking-tight">
        Fale com a gente para conhecer os planos
      </p>
      <p className="mt-3 text-sm leading-6 text-white/55">
        Nossa equipe apresenta as opções e indica o plano certo para o seu negócio.
      </p>
      <Button asChild size="lg" className="mt-7 h-12 w-full rounded-xl">
        <a
          href={
            DEMO_WHATSAPP
              ? `https://wa.me/${DEMO_WHATSAPP}?text=${encodeURIComponent("Olá, Innovally! Quero conhecer os planos.")}`
              : "#contato"
          }
          target={DEMO_WHATSAPP ? "_blank" : undefined}
          rel={DEMO_WHATSAPP ? "noreferrer" : undefined}
        >
          Conhecer os planos <ArrowUpRight className="h-4 w-4" />
        </a>
      </Button>
    </Card>
  );
}

function FaqSection() {
  return (
    <section
      id="faq"
      className="landing-light scroll-mt-28 bg-background px-5 py-28 text-foreground sm:px-8 sm:py-36 lg:px-10"
    >
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24">
        <ScrollReveal>
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
            Perguntas frequentes
          </p>
          <h2 className="max-w-md font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
            Antes de começar, tudo claro.
          </h2>
          <p className="mt-6 max-w-sm text-base leading-7 text-muted-foreground">
            Se ainda ficou alguma dúvida, fale com a gente pelo WhatsApp. A primeira conversa é
            simples e sem compromisso.
          </p>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <FaqList />
        </ScrollReveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      id="contato"
      className="scroll-mt-28 bg-[#f5efe8] px-5 py-24 text-[#171717] sm:px-8 sm:py-32 lg:px-10"
    >
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-start lg:gap-24">
        <ScrollReveal>
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#171717]/55">
            Seu próximo turno começa aqui
          </p>
          <h2 className="max-w-xl font-display text-4xl font-semibold leading-[0.98] tracking-[-0.06em] sm:text-6xl">
            Vamos colocar sua operação em movimento?
          </h2>
          <p className="mt-6 max-w-md text-base leading-7 text-[#171717]/65">
            Conte um pouco sobre o seu restaurante. A gente abre a conversa, mostra a plataforma e
            explica como funciona o plano completo de R$ 79,90 por mês.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm font-medium text-[#171717]/70">
            <Zap className="h-4 w-4" /> Resposta pelo WhatsApp
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <Card className="rounded-[1.7rem] border-black/10 bg-white p-5 text-foreground shadow-2xl shadow-black/10 sm:p-8">
            <LeadForm />
          </Card>
        </ScrollReveal>
      </div>
    </section>
  );
}

function MobileStickyCta() {
  return (
    <div className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-40 lg:hidden">
      <a
        href="#contato"
        className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-2xl shadow-primary/25 transition-transform active:scale-[0.98]"
      >
        Conhecer o plano <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  );
}
